/**
 * UDE (User Data Extractor) API client — talks to FastAPI on :7004
 * via vite proxy `/ude-api/*` → `http://localhost:7004/api/*`.
 *
 * Hand-written to match the OpenAPI schema exposed at
 * http://localhost:7004/openapi.json. When backend shapes change,
 * regenerate this file by inspecting the actual responses.
 */
import {AxiosInstance} from 'axios';
import {authFetch} from '../auth/session';
import type {UdeRoofType} from '../components/ude/roofTypes';

// ------------------------------------------------------------------
// Types — mirror UDE's Pydantic schemas
// ------------------------------------------------------------------

export type DocType =
    | 'utregning' | 'situasjonskart' | 'utomhusplan' | 'avkjorselsplan'
    | 'floor_plan' | 'roof_plan' | 'section' | 'facade'
    | 'perspective_3d' | 'garage_plan' | 'driveway_section' | 'form'
    | 'unknown';

export type Confidence = 'high' | 'medium' | 'low';

export type ExtractionStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface UdeProject {
    id: string;
    label: string;
    street: string | null;
    number: string | null;
    kommune_code: string | null;
    gnr: number | null;
    bnr: number | null;
    created_at: string;
    updated_at: string;
}

export interface UdeProjectCreate {
    label: string;
    street?: string;
    number?: string;
    kommune_code?: string;
    gnr?: number;
    bnr?: number;
}

/**
 * Partial update payload for PATCH /api/projects/{id}.
 *
 * Each property is independently optional. ``null`` is a real value
 * (clears the column); omitting a key leaves it untouched. Use
 * ``undefined`` (omission) to keep a field; use ``null`` to wipe it.
 */
export interface UdeProjectPatch {
    label?: string | null;
    street?: string | null;
    number?: string | null;
    kommune_code?: string | null;
    gnr?: number | null;
    bnr?: number | null;
}

export interface UdeDocument {
    document_id: number;
    original_filename: string;
    sha256: string;
    size_bytes: number;
    uploaded_at: string;
    doc_type: DocType | null;
    building_id: string | null;
    confidence: Confidence | null;
    reasoning: string | null;
    title_block: Record<string, unknown> | null;
    model_used: string | null;
    classified_at: string | null;
    user_corrected: boolean;
    /** UDE 23.7a — the drawing-version axis for endring (delta) projects:
     *  «Eksisterende fasade» / «Ny fasade» / rives-marked sheets. Null = no
     *  version marker (the normal nybygg case — absolute end state). Surfaced
     *  only for now; pair-aware rendering lands with 23.7c. */
    drawing_version?: 'eksisterende' | 'ny' | 'rives' | null;
    // Phase 7 Ticket 3c — extraction status surfaced from the latest
    // ude.drawing_extraction row. Drives the failed-row indicator and
    // ``Prøv igjen`` button on the Drawings page.
    extraction_status: 'pending' | 'extracted' | 'failed';
    extraction_error: string | null;
    // Phase 15.5 — the latest bundle classification (ude.document_extraction),
    // set once the bundle pass runs at extraction. ``document_kind`` non-null
    // marks this as a non-drawing bundle document, lifted into the separate
    // "Dokumenter" lane; null = a drawing or a not-yet-classified upload.
    document_kind: 'evidence' | 'correspondence' | 'reference' | null;
    bundle_type: string | null;
    /** True once a human has overridden the detected bundle_type (0009). */
    bundle_type_user_corrected?: boolean;
}

export interface UdeBatchRetryResponse {
    status: 'done' | 'noop';
    retried: number;
    succeeded: number;
    failed: number;
}

export interface UdeRetryExtractionResponse {
    status: 'extracted' | 'failed';
    doc_id: number;
    doc_type?: string;
    building_id?: string | null;
    warnings_count?: number;
    elapsed_s?: number;
    model_used?: string;
    error?: string;
}

/** A file rejected at intake — not stored, not classified. Phase 15 PDF-only:
 *  non-PDFs are surfaced per-file (non-fatal) so the UI can warn near the file. */
export interface UdeRejectedFile {
    filename: string;
    reason: string;  // currently always 'unsupported_type'
}

export interface UdeUploadResponse {
    documents: UdeDocument[];
    classified: number;
    failed: number;
    /** Phase 15.5 — per-file intake rejections (e.g. a non-PDF). Non-fatal. */
    rejected: UdeRejectedFile[];
}

export interface UdeClassificationPatch {
    doc_type?: DocType;
    building_id?: string | null;
    /** A non-drawing document's bundle_type. Persisted on the per-document
     *  classification row, so it survives extraction-run pruning (0009). */
    bundle_type?: string;
}

export interface UdeExtractionRun {
    id: string;
    project_id: string;
    status: ExtractionStatus;
    progress_total: number;
    progress_done: number;
    started_at: string | null;
    finished_at: string | null;
    error: string | null;
    model_versions: Record<string, string> | null;
}

export interface UdeExtractionRunStart {
    run_id: string;
    status_url: string;
}

export interface UdeConfig {
    version: string;
    dev_tools: boolean;
}

// ------------------------------------------------------------------
// ProjectFacts (GET /facts) — mirrors user_data_extractor/models.py
// ------------------------------------------------------------------

export interface UdeAddress {
    street: string | null;
    number: string | null;
    kommune_id: string | null;
    gnr: number | null;
    bnr: number | null;
}

export interface UdeRoomArea {
    name: string;
    area_m2: number;
    floor: string | null;
}

export interface UdeFloorKote {
    floor: string;
    kote: number;
}

// ------------------------------------------------------------------
// Phase 6 — typed warnings (replaces flat string[])
// ------------------------------------------------------------------

export type UdeWarningKind =
    | 'cross_building_label_mismatch'
    | 'foreign_context_reference'
    | 'typo_correction'
    | 'missing_label'
    | 'conflict_between_drawings'
    | 'unreadable_value'
    | 'computed_input_suspect'
    | 'template_reuse'
    | 'missing_drawing'
    | 'duplicate_content'
    | 'missing_field'
    | 'regulatory_max_exceeded'
    | 'address_mismatch'
    | 'other';

export type UdeWarningSeverity = 'info' | 'warning' | 'blocker';

export type UdeActionCategory =
    | 'du_kan_rette'
    | 'krever_flere_tegninger'
    | 'krever_utvikler'
    | 'manuell_handling';

export type UdeWarningStatus = 'open' | 'accepted' | 'dismissed' | 'resolved';

/**
 * Typed warning record. Replaces the old flat string[] shape.
 *
 * Field grouping mirrors the backend `Warning` Pydantic model in
 * user_data_extractor/models.py:
 *   - categorical: kind, severity, action_category
 *   - message: message_no (Norwegian, ≤200 chars)
 *   - locator (what): affected_field, affected_value_*, suggested_correction
 *   - source (where): affected_drawing_id, page_number, bbox_xywh_normalized
 *   - resolution: status, warning_hash, reviewed_*
 */
/**
 * One source drawing's value for the field a conflict warning disputes.
 * Merger-authored evidence (2026-06-10): lets the UI show value-labelled
 * file chips, highlight the value on the PDF (Tier 1 via `value_label`),
 * and offer pick-a-source correction. Empty on non-conflict warnings.
 */
export interface UdeValueCandidate {
    drawing_id: string | null;
    page_number: number;
    value_numeric: number | null;
    /** Literal value text for Tier 1 substring search on this drawing. */
    value_label: string | null;
    /** DocType of the source drawing, e.g. "section" / "facade" / "utregning". */
    doc_type: string | null;
    /** Unique foreign building whose resolved value byte-matches this candidate. */
    matches_building: string | null;
    /**
     * Plausibility-gated: physically impossible for the host building
     * (e.g. a gesims 14 m over its own terrain — typically a context-volume
     * label from a neighboring building). Kept as evidence, excluded from
     * resolution; `note` carries the Norwegian reason.
     */
    implausible?: boolean;
    /** Norwegian display text, e.g. "Identisk med Hus A sin mønekote". */
    note: string | null;
}

export interface UdeWarning {
    kind: UdeWarningKind;
    severity: UdeWarningSeverity;
    action_category: UdeActionCategory;
    message_no: string;

    /** JSON pointer, e.g. "/buildings/2/kote_avg_terrain". */
    affected_field: string | null;
    affected_value_numeric: number | null;
    /** Literal text on the PDF — used for Tier 1 substring search. */
    affected_value_label: string | null;
    suggested_correction: string | null;

    affected_drawing_id: string | null;
    page_number: number | null;
    /** Normalized 0–1 [x, y, w, h]. Flash-only; Lite leaves null. */
    bbox_xywh_normalized: number[] | null;
    /**
     * Phase 7 Ticket 2 — other drawings that emitted the same warning
     * text. Post-merger annotation only; the LLM never populates this.
     * Empty when the warning fired on a single drawing.
     */
    additional_drawing_ids?: string[];
    /** Pages parallel to ``additional_drawing_ids`` (same length / order). */
    additional_pages?: number[];
    /**
     * Per-source evidence for conflict_between_drawings — every drawing
     * that supplied a value for the disputed field, with its value.
     * Merger-authored; empty/absent for all other warning kinds.
     */
    value_candidates?: UdeValueCandidate[];

    status: UdeWarningStatus;
    /** Stable hash; used as key for resolution + locator-cache lookups. */
    warning_hash: string;
    reviewed_at: string | null;
    reviewed_by: string | null;
    note: string | null;
}

/** Phase 14.2: building-census provenance. The architect's inconsistent
 *  per-sheet labels (Hus A / Nytt hus / Hovedhus) folded into one canonical
 *  building; `aliases` are the original labels (≠ canonical id), `kind` the
 *  census's free-text classification (enebolig / garasje / …). Absent when the
 *  census didn't run or was a no-op. */
export interface UdeBuildingCensus {
    aliases: string[];
    kind: string | null;
    confidence: string | null;
}

export interface UdeBuildingFacts {
    building_id: string;
    /** Phase 14.2: census provenance — folded aliases + kind. See UdeBuildingCensus. */
    census?: UdeBuildingCensus | null;
    bya_m2: number | null;
    bra_total_m2: number | null;
    bra_per_floor: Record<string, number>;
    /** Phase 10: dwelling-unit count (boenheter). Not on drawings — set by the
     *  user on Fakta. null/absent = unknown (BRA_PER_ENHET stays cant_determine). */
    boenheter: number | null;
    /** Phase 10 follow-up: LLM-estimated boenheter candidate from the floor
     *  plans (+ basis). Pre-fills the Fakta field as a «foreslått — bekreft»
     *  suggestion; confirming writes `boenheter`. Absent on older facts. */
    boenheter_suggested?: number | null;
    boenheter_suggested_basis?: string | null;
    kote_mone: number | null;
    kote_gesims: number | null;
    kote_avg_terrain: number | null;
    floor_kotes: UdeFloorKote[];
    gesims_height_m: number | null;
    mone_height_m: number | null;
    /** SB-138: the tiltak's LOWEST point — the datum «tiltakets totale høyde (HT)»
     *  is measured from (S-4220 § 10 b, S-4644 § 3.1). Set by the merger only where
     *  the sheet's printed HT dimension and the geometric read of the drawn datum
     *  line agree; otherwise `_suggested` carries the one reading there is, with
     *  its basis, as a «Foreslått — Bruk» offer. User-correctable like the other
     *  kotes. Absent on facts extracted before SB-138. */
    kote_terrain_min?: number | null;
    kote_terrain_min_basis?: string | null;
    kote_terrain_min_suggested?: number | null;
    kote_terrain_min_suggested_basis?: string | null;
    /** SB-272: the building's shortest distance to its plot's own boundary, as a PERSON
     *  confirmed it — what items 1.5 / 2.4 (PBL § 29-4) decide on. Never set by extraction;
     *  `_suggested` is the situasjonskart's shortest tomtegrense distance for the building,
     *  derived on every read, offered as «Foreslått — Bruk». */
    avstand_tomtegrense_m?: number | null;
    avstand_tomtegrense_suggested?: number | null;
    avstand_tomtegrense_suggested_basis?: string | null;
    /** SB-138: per-section outcomes of the datum read, counted per token
     *  (`read` · `no_ruler` · `no_average` · `no_line_below` · `implausible` ·
     *  `error` · `not_read`), so a blank says what the sections lack. */
    terrain_datum_reads?: Record<string, number> | null;
    /** UDE 25.1: roof form — the FIFTH user-confirmed parameter. Selects a legal
     *  branch (the 24.3 ruling makes the måleregel depend on it), so it is never
     *  auto-set: null until a human confirms. Absent on older facts.
     *  Vocabulary is a cross-service contract — see components/ude/roofTypes. */
    roof_type?: UdeRoofType | null;
    /** Suggestion only, never read by a verdict. Set from a roof form printed as
     *  a WORD, or from a gated read of the SECTION sheets. A printed angle never
     *  populates it — see `takvinkel_text`. */
    roof_type_suggested?: UdeRoofType | null;
    roof_type_suggested_basis?: string | null;
    /** Verbatim roof-angle annotation as printed («Takvinkel: 5 Grader»).
     *  EVIDENCE for the human, not a suggestion: an angle cannot determine a
     *  form, since a symmetric saltak has one pitch too. */
    takvinkel_text?: string | null;
    /** SB-65: how the sections/facades were READ for roof form, counted per
     *  outcome — keys are `UdeRoofFormRead` (components/ude/roofTypes). Lets a
     *  blank suggestion say whether the sections were unreadable or the calls
     *  failed. Absent on facts extracted before 25.1. */
    roof_form_reads?: Record<string, number> | null;
    /** 23.7b: the plan's PERMITTED maxima as printed on the sheet
     *  («Mks GH = +11»), kote scale — claims about the plan, never actuals.
     *  Absent on facts extracted before this shipped. */
    regulatory_max_gesims?: number | null;
    regulatory_max_mone?: number | null;
    rooms: UdeRoomArea[];
    source_drawings: string[];
    /** Per-field provenance: field name (bya_m2, kote_mone, …) → drawing(s)
     *  that carried the resolved value. Field-specific, unlike source_drawings.
     *  Absent on facts extracted before this shipped — treat missing as {}. */
    field_sources?: Record<string, string[]>;
    /** Phase 6: typed Warning records (replaces flat string[]). */
    warnings: UdeWarning[];
}

export interface UdeOutdoorArea {
    name: string;
    area_m2: number;
}

export interface UdeClassifiedDistance {
    value_m: number;
    category: string;
    from: string | null;
    to: string | null;
    anchor_hint: string | null;
    notes: string | null;
}

export interface UdeSiteFacts {
    total_bya_m2: number | null;
    mua_ground_m2: number | null;
    takterasse_m2: UdeOutdoorArea[];
    parking_spots: number | null;
    distances: UdeClassifiedDistance[];
    unclassified_dimensions: Array<{value_m: number; approx_location: string | null}>;
    infrastructure: string[];
    outdoor_areas: UdeOutdoorArea[];
    /** Phase 10: plot area (m²). Resolved from LCF at extraction, user-
     *  overridable on Fakta. null = LCF had no value. Drives percent_bya. */
    tomteareal_m2: number | null;
    /** Existing buildings on the plot OUTSIDE this søknad (m² BYA) — grad av
     *  utnytting counts all bebyggelse, but the drawings only show the
     *  submitted tiltak. Caseworker-supplied on Fakta («fra matrikkelen»);
     *  null = not provided → percent_bya covers the søknad only. */
    existing_bya_m2: number | null;
    /** Phase 16: automated suggestion for existing_bya_m2 — bundle-doc claim
     *  (16.1) or LCF map-footprint estimate (16.2). Never auto-applied; the
     *  user clicks «Bruk». basis carries source + caveat for the tooltip. */
    existing_bya_suggested: number | null;
    existing_bya_suggested_basis: string | null;
    /** SB-467 — when the documents state different existing-BYA figures, none
     *  is suggested from them (the map estimate stands); this names each figure,
     *  so the user picks. Empty while they agree. */
    existing_bya_disagreement?: UdeStatedExistingBya[] | null;
    // Phase 21.2 — plot area stated on the situasjonskart, surfaced as a
    // «Foreslått fra tegningene — Bruk» suggestion (LCF stays authoritative).
    tomteareal_suggested: number | null;
    tomteareal_suggested_basis: string | null;
    /** SAK10 § 4-1 — height of the TALLEST retaining wall, in metres.
     *  User-confirmed: the value selects a legal branch (søknadsplikt or not),
     *  so nothing writes it but a person. null = not stated, the default. */
    stottemur_hoyde_m: number | null;
    /** SB-75 — proposed from the wall's own section sheets (the tallest printed
     *  height dimension, max across sheets). Never auto-applied; the user clicks
     *  «Bruk». basis names the sheets + the «tidligere godkjent» caveat. */
    stottemur_hoyde_suggested: number | null;
    stottemur_hoyde_suggested_basis: string | null;
    /** Per-outcome counts of the wall-sheet reads (`read` · `not_a_wall_sheet` ·
     *  `no_dimensions` · `error` · `not_read`) — what makes a BLANK suggestion
     *  legible: no wall sheet at all, or a wall sheet that printed no height. */
    stottemur_hoyde_reads: Record<string, number>;
    /** SB-281 — the shortest distance from a building in the søknad to the road's
     *  centre line, as a person confirmed it: what vegloven § 29 (1.2 / 2.5)
     *  decides on. Absent on rows stored before it existed. */
    avstand_midte_vei_m?: number | null;
    /** Always null since SB-334: the drawings' road-centre readings are no longer
     *  suggested (on prod every one was wrong). Kept so Fakta's «Bruk» branch, which
     *  renders only for a non-null value, needs no change if the suggestion returns. */
    avstand_midte_vei_suggested?: number | null;
    avstand_midte_vei_suggested_basis?: string | null;
    percent_bya: number | null;
    utnyttelsesgrad: number | null;
    /** Phase 6: site-scoped typed warnings (cross-building leaks, duplicates, etc.). */
    warnings: UdeWarning[];
}

export interface UdeValidationInfo {
    missing_fields: string[];
    conflicts: string[];
    /** Phase 6: union of all building + site warnings, deduplicated by hash. */
    qa_warnings: UdeWarning[];
    duplicate_drawings: string[];
}

/** SB-459 — one tiltak the bundle's documents state when they disagree: the
 *  type it resolves to, how many documents state it, their bundle types and
 *  file names, and the first one's quote. */
export interface UdeStatedTiltakstype {
    tiltakstype: string;
    documents: number;
    sources: string[];
    files: string[];
    quote: string;
}

/** SB-467 — one existing-BYA figure the documents state when they disagree, in
 *  the same shape as a stated tiltak. */
export interface UdeStatedExistingBya {
    value: number;
    documents: number;
    sources: string[];
    files: string[];
    quote: string;
}

export interface UdeProjectFacts {
    address: UdeAddress | null;
    buildings: UdeBuildingFacts[];
    site: UdeSiteFacts;
    validation: UdeValidationInfo;
    extracted_at: string | null;
    source_documents: string[];
    /** Phase 14.3: one grounded Norwegian summary sentence for the Fakta
     *  overview header. Best-effort at extraction; absent/null when it failed
     *  or there was nothing to summarize. */
    overview_narrative?: string | null;
    /** Phase 23.2 — project type (tiltakstype). `tiltakstype` is the CONFIRMED
     *  value (user-set on Fakta; drives the SAK10 § 5-4 required-docs set on
     *  /completeness). The *_suggested triple is detection output — drawing
     *  signals or a bundle-stated «tiltakets art» — offered as «Foreslått —
     *  Bruk», never auto-applied. Optional: rows persisted before 23.2 lack
     *  the keys. */
    tiltakstype?: string | null;
    tiltakstype_suggested?: string | null;
    tiltakstype_suggested_basis?: string | null;
    tiltakstype_suggested_confidence?: string | null;
    /** SB-101 — the confirmed SET (selection order). The home whenever it is
     *  non-empty; `tiltakstype` above is then derived on read as the governing
     *  member (strictest regime). A row from before SB-101 carries only the
     *  singular, and the backend widens it to a one-element list on read. */
    tiltakstyper?: string[] | null;
    /** SB-101 — detection may propose several at once (a søknadsskjema ticks
     *  several boxes); the `*_suggested` triple stays for the drawing ladder. */
    tiltakstyper_suggested?: string[] | null;
    /** SB-459 — when the bundle's documents state different tiltak, none is
     *  suggested from them (the drawing side's suggestion stands); this names
     *  what each says, so the user picks. Empty while they agree. */
    tiltakstype_disagreement?: UdeStatedTiltakstype[] | null;
    /** UDE 24.7c — SAK10 § 6-8. `null`/absent = not stated, which is a real
     *  third state and NOT «no»: only an explicit `true` adds the
     *  kvalifikasjons-documentation to the required set. Nothing suggests it —
     *  it is a choice the tiltakshaver makes, so no drawing can carry it. */
    selvbygger?: boolean | null;
    /** UDE 24.5a / A3 — which application STAGE the case is at (SAK10 § 5-4
     *  fjerde ledd). `null`/absent = not stated, and that is a real third state:
     *  the verdict then names NO stage rather than assuming ett-trinn, which is
     *  the claim the lawyer calls *misvisende*. **User-set, never guessed** —
     *  ruling 2026-08-25 §D: «Dette bør være et eksplisitt felt og ikke noe
     *  programmet forsøker å gjette … Det skal ikke gjette.»
     *  OPEN vocabulary, same as the completeness mirror below. */
    soknadstrinn?: string | null;
}

// ------------------------------------------------------------------
// Drawing QA + corrections + validate
// ------------------------------------------------------------------

export type QaSeverity = 'blocker' | 'warning' | 'info';

export interface UdeQaFinding {
    rule_id: string;
    severity: QaSeverity;
    message: string;
    affected_drawings: string[];
    where_to_check: string | null;
    /** Phase 10 follow-up — deep-link identity (Samsvar → Fakta). Absent on
     *  findings produced before this shipped. */
    warning_hash?: string | null;
    scope?: string | null;
    affected_field?: string | null;
}

export interface UdeDrawingQa {
    summary: {blocker: number; warning: number; info: number};
    findings_by_severity: {
        blocker: UdeQaFinding[];
        warning: UdeQaFinding[];
        info: UdeQaFinding[];
    };
}

/** SB-228 — how a Fakta value came to be: `suggestion` = what «Bruk» filled in, saved
 *  unchanged; `entered` = typed or picked by a person (a suggestion accepted and then edited is
 *  `entered`). The backend 422s any other token; an op without one is recorded as unknown. */
export type UdeFactOrigin = 'suggestion' | 'entered';

export interface UdeJsonPatchOp {
    op: 'add' | 'remove' | 'replace' | 'move' | 'copy' | 'test';
    path: string;
    value?: unknown;
    from?: string;
    /** SB-228 — stated by the client, which alone knows whether a click or a keyboard put the
     *  value there. */
    origin?: UdeFactOrigin;
    /** SB-228 — the suggestion the facts showed for this field, stamped by the backend at save.
     *  Never sent: the server replaces whatever a client puts here. */
    suggested?: unknown;
}

export interface UdeFactCorrection {
    patch: UdeJsonPatchOp[];
    reason?: string | null;
    author?: string | null;
}

export interface UdeValidateRequest {
    tomteareal_m2?: number;
    enheter_per_building?: Record<string, number>;
    extraction_run_id?: string;
}

export interface UdeValidateResponse {
    project_id: string;
    total_items: number;
    by_status: Record<string, number>;
    /** C1 — non-pass results on `tegnekvalitet` items, kept OUT of `by_status`
     *  so they cannot read as «mangler i søknaden». */
    tegnekvalitet_findings?: number;
    by_source: Record<string, number>;
}

/** GET /validate/status — compliance-run state for the «Kjører… N/M» button +
 *  refresh-recovery (option B). `running=false` with a recent `finished_at`
 *  means just-completed; `error` set means the run failed. */
export interface UdeValidateStatus {
    running: boolean;
    started_at: string | null;
    finished_at: string | null;
    done: number;
    total: number;
    error: string | null;
}

/**
 * `GET /projects/{id}/runs/active` — both long jobs in one call.
 *
 * `null` means «not running», which is NOT «never ran» — that question is
 * `GET /summary`'s. The two are reported together because they also exclude
 * each other: an extraction replaces the facts a compliance run reads.
 */
export interface UdeActiveRuns {
    extraction: {
        run_id: string;
        status: string;
        done: number;
        total: number;
        /** Lets the card estimate remaining time from the run's own rate. */
        started_at: string | null;
    } | null;
    validation: {
        done: number;
        total: number;
    } | null;
    /**
     * Each job's newest FINISHED run, from the database (SB-26). A failure stays
     * readable after a page load or a sign-in, and a samsvarssjekk lost in a
     * restart leaves no newer row. Absent from a UDE older than SB-26.
     */
    last_extraction?: {
        run_id: string;
        status: string;
        finished_at: string | null;
        error: string | null;
    } | null;
    last_validation?: {
        started_at: string;
        finished_at: string | null;
        error: string | null;
    } | null;
}

// ------------------------------------------------------------------
// Phase 5 part 2 — checklist payloads (GET /checklist response)
// ------------------------------------------------------------------

export type ComplianceVerdict = 'pass' | 'fail' | 'cant_determine' | 'not_applicable';

export type ComplianceCategory = 'auto' | 'visual' | 'hybrid' | 'external';

/** How a checklist item is evaluated. A copy of
 *  `successbuilder_shared.vocabulary.CHECK_TYPES`, pinned by
 *  `scripts/vocabulary_parity.py` — widen both in the same commit (SB-112). */
export type ComplianceCheckType =
    | 'numeric_compare' | 'llm_reasoning' | 'visual' | 'external' | 'manual'
    // SB-106 — a content point on a non-drawing document (`bundle_doc_types`).
    | 'document_content'
    // SB-109 — two document types compared against each other (DiBK 17.10).
    | 'document_consistency'
    // SB-116 — a plan-provision check: the parameter is resolved for the address first,
    // a case fact gates it, and only a bound provision can turn drawing evidence into a verdict.
    | 'plan_provision';

/** C1 — what a FAILURE of a checklist item means. A copy of
 *  `successbuilder_shared.vocabulary.TIERS`, pinned by `scripts/vocabulary_parity.py`
 *  — widen both in the same commit (SB-112). The per-value meaning is documented
 *  on `UdeChecklistItem.tier`, where it is read. */
export type UdeChecklistTier = 'krav' | 'hjelp_til_vurdering' | 'tegnekvalitet' | 'uavklart';

/** SB-108 — presence × sufficiency of a required søknadsdokument, one token per
 *  row of `GET /completeness`. A copy of
 *  `successbuilder_shared.vocabulary.DOC_SUFFICIENCY_STATES`, pinned by
 *  `scripts/vocabulary_parity.py` — widen both, and `SUFFICIENCY_NO` in
 *  `bundleTypes.ts`, in the same commit. Only the first and the fourth are a
 *  mangel; the API says which with `is_mangel`, so nothing here re-derives it.
 *  `foreligger_ikke_kontrollert` is NOT an all-clear and never renders as ✓. */
export type UdeDocSufficiencyState =
    | 'mangler'
    | 'foreligger_ikke_kontrollert'
    | 'foreligger_tilstrekkelig'
    | 'foreligger_mangler_opplysninger'
    | 'foreligger_maa_kontrolleres';

/** How a checklist row's verdict was produced — the lane. A copy of
 *  `successbuilder_shared.vocabulary.UI_SOURCES`: every lane the backend writes
 *  (`SOURCES`, guarded by the DB CHECK) plus `not_evaluated`, the placeholder
 *  the compliance page synthesises for an item with no result yet — never
 *  written, and the CHECK is right to reject it. Pinned by
 *  `scripts/vocabulary_parity.py` — widen both in the same commit (SB-113). */
export type ComplianceSource =
    | 'numeric_compare' | 'llm_reasoning' | 'visual' | 'external_lookup'
    | 'user_override' | 'manual'
    // Phase 15 — the row was resolved/surfaced from a non-drawing bundle
    // document (document-present pass, or a dispensasjon status surface).
    | 'bundle'
    // UDE 23.5 — the dispatch filter wrote a visible «gjelder ikke tiltakstypen» row.
    | 'matrix'
    // Frontend-only: the «Ingen evaluering ennå» placeholder row (components.tsx).
    | 'not_evaluated';

/** Phase 9.4 — who-can-fix bucket for an unresolved (cant_determine) row.
 *  ``null`` on resolved rows. */
export type CoverageClass =
    | 'caseworker' | 'authoring' | 'admin_authoring' | 'admin';

/** A single checklist item's evaluation in one scope (site or per-building). */
export interface UdeChecklistResult {
    /** Scope key: ``"_site"`` for site-level rows, otherwise a building_id like ``"Hus A"``. */
    scope: string;
    status: ComplianceVerdict;
    /** Polymorphic — number / bool / string / list / null. UI must render each cleanly. */
    value: unknown;
    unit: string | null;
    reason: string | null;
    where_to_check: string | null;
    confidence: Confidence | null;
    source: ComplianceSource;
    /** Phase 26.7 — this verdict rests, at least partly, on a document the
     *  applicant uploaded rather than on the regulation corpus. Always present
     *  once any user document grounds anything on the project; absent means the
     *  project has none (NOT «unknown»). `reason` already carries the same
     *  qualifier in prose, because a chip does not survive copy-paste, plain
     *  text export, or a screenshot — and the reader who most needs this signal
     *  is a saksbehandler holding a report someone else produced. */
    user_sourced?: boolean;
    provenance_note_no?: string;
    sources: {
        /** Drawing refs as the compliance engine emits them. Live payloads carry
         *  ``tegningsnr`` (+ ``building_id``); older/synthetic shapes may carry a
         *  numeric ``doc_id`` or ``original_filename``. Resolvers must try all. */
        drawings?: Array<{
            doc_id?: string | number;
            tegningsnr?: string;
            building_id?: string;
            drawing_type?: string;
            page?: number;
            original_filename?: string;
        }>;
        /** is_plan marks reguleringsplan ids (e.g. "S-2705") that are NOT
         *  reg_sections. planid (when the plan has a fetchable PDF) links the
         *  chip to UDE's plan-PDF proxy; without it the chip is non-clickable. */
        regulations?: Array<{section_id: string; citation?: string; is_plan?: boolean; planid?: number}>;
        /** Phase 15 — bundle document types that resolved/affected this row
         *  (e.g. ``["reguleringsbestemmelser"]`` or the dispensasjon parameter). */
        bundle?: string[];
        /** SB-107 / SB-109 — the non-drawing documents a content point or a
         *  cross-document comparison actually READ, one entry per document
         *  with what was seen in it. `status` is open vocabulary — the
         *  reading states of a point (present / empty / not_in_document /
         *  unreadable / error / disagreement / unanswered) or of a comparison
         *  side (read / matched / unmatched / skipped). */
        documents?: Array<{
            doc_id?: number | string | null;
            bundle_type?: string | null;
            original_filename?: string | null;
            status?: string | null;
            quote?: string | null;
            page?: number | null;
            verified?: boolean | null;
            note?: string | null;
        }>;
    } | null;
    /** Phase 9.4 — who-can-fix bucket; only set on cant_determine rows. */
    coverage_class?: CoverageClass | null;
    last_checked: string | null;
}

/**
 * Definition of one checklist item (43 in total) plus its current
 * results across scopes. ``results`` is empty when ``/validate`` has
 * not been run yet for this project.
 */
export interface UdeChecklistItem {
    item_id: string;
    section_name: string;
    description: string;
    category: ComplianceCategory;
    check_type: ComplianceCheckType;
    /** C1 (ruling 2026-08-25 §A) — what a FAILURE of this item means. Three
     *  values where the ruling names two, because his own source has a middle
     *  one: every drawing-content check in DiBK's national lists is a
     *  `Hjelp til vurdering`, not a standalone requirement.
     *
     *  - `krav` — a real requirement or a material plan breach; a mangel/avvik
     *  - `hjelp_til_vurdering` — nationally listed, feeds the judgement about
     *    whether a drawing is sufficient, **never a mangel on its own**
     *  - `tegnekvalitet` — no source; a house convention. Renders as ADVICE and
     *    must never affect the completeness conclusion:
     *    «Det siste bør ikke påvirke konklusjonen om søknaden er komplett».
     *  - `uavklart` — not yet sourced (the default, and 15 of 46 still are).
     *    Behaves exactly as before the tiering. */
    tier?: UdeChecklistTier;
    /** Where the tier came from — the DiBK check, or why there is no source. */
    tier_note?: string | null;
    related_parameter_codes: string[];
    drawing_types: string[];
    /** SB-105/106 — the non-drawing twin of `drawing_types`: the bundle document
     *  types a content point reads (BundleDocType values). Empty for drawing items. */
    bundle_doc_types?: string[];
    /** SB-105/106 — the DiBK lists (rs · et · ta · es · ig · mb · fa · nv) that
     *  carry this point. Empty = does not vary by søknadstype. */
    applicable_soknadstyper?: string[];
    display_order: number;
    results: UdeChecklistResult[];
}

/** Phase 23.4 — one søknadsplikt/regime advisory line. `kind` styles the icon.
 *  Two of them warn, because both guard the asymmetric failure direction — a
 *  søknad that reads complete without the ansvarsrett it needs:
 *  `track_condition_violated` (the facts break the confirmed type's own track
 *  conditions) and `r2_conditions_unverified` (the R2 track rests on a § 3-1
 *  vilkårssett we can only partly check — the 2026-08-17 lawyer ruling made
 *  this explicit). The rest are informational: exemption / lighter track
 *  possible, a skjønn note (§ 20-5 f karakterendring, § 20-4 e kommunal
 *  vurdering), and a saksbehandling note (the 12-ukersfrist a dispensasjon
 *  triggers). `conditions` are the standing conditions the advisory depends on
 *  («ikke beboelse», plansamsvar, the full vilkårssett …).
 *
 *  Mirrors the constants in `compliance/regime_precheck.py`; the renderer falls
 *  back to the info icon for an unrecognised kind, so a lagging mirror degrades
 *  rather than breaks — widen both in the same commit anyway. */
export interface UdeRegimeFinding {
    kind: 'exemption_possible' | 'lighter_track_possible'
        | 'track_condition_violated' | 'r2_conditions_unverified'
        | 'skjonn_note' | 'saksbehandling_note' | 'selvbygger_possible';
    message_no: string;
    section_refs: string[];
    conditions: string[];
    /** SB-101 — the component(s) of a multi-type project this line speaks for.
     *  Empty/absent on a single-type project and on lines about the whole. */
    tiltakstyper?: string[];
}

/** Phase 23.4 — the pre-check block on GET /checklist. Present only once a
 *  tiltakstype is confirmed on Fakta; `findings` empty ⇒ facts and the type's
 *  track agree ⇒ the panel renders nothing. Advisory-only: never a verdict. */
export interface UdeRegimePrecheck {
    tiltakstype: string;
    default_regime: string;   // R1 | R2 | R3 (internal label — not shown as a badge)
    findings: UdeRegimeFinding[];
    /** SB-101 — the whole confirmed set; `tiltakstype` is its governing member. */
    tiltakstyper?: string[];
    /** SB-101 — the state of «én samlet byggesak?»; null on a single-type project. */
    samlet_byggesak?: 'ja' | 'nei' | 'vet_ikke' | 'ikke_besvart' | (string & {}) | null;
}

export interface UdeChecklistResponse {
    project_id: string;
    items: UdeChecklistItem[];
    /** Phase 9.4 — count of unresolved rows per who-can-fix bucket (zero-count
     *  buckets omitted). Drives the coverage banner on the Samsvar report. */
    coverage_summary?: Partial<Record<CoverageClass, number>>;
    /** Phase 23.4 — søknadsplikt/regime advisory (null until a tiltakstype is
     *  confirmed, or when the best-effort computation failed). */
    regime_precheck?: UdeRegimePrecheck | null;
    /** Phase 23.5 — the «typespesifikke kontroller mangler» caveat: set when a
     *  non-founding tiltakstype is confirmed and no type_specific checklist
     *  content exists for it yet — the report covers only the applicable core
     *  baseline and must not read as full coverage. Null otherwise. */
    type_content_pending?: {
        tiltakstype: string;
        /** SB-101 — every confirmed type still lacking content. */
        tiltakstyper?: string[];
        applicable_items: number;
        core_total: number;
    } | null;
    /** SB-346 — no tiltakstype is confirmed, so the type-specific checks wait
     *  (SB-342) and the report covers the core set only. Carries the types the
     *  drawings suggest (medium or high confidence), possibly none. Null when a
     *  type is confirmed, before there are facts, or when nothing waits. */
    type_specific_waiting?: {
        suggested_tiltakstyper: string[];
    } | null;
    /**
     * Is this report still about the facts on Fakta?
     *
     * A re-extraction leaves every verdict on screen unchanged and silently
     * obsolete, so the answer has to be STATED — nothing in the rows reveals
     * it. `unknown` is not a synonym for `current`: run retention drops the
     * provenance stamp, and «we cannot tell» is the honest reading.
     */
    freshness?: {
        state: 'never_run' | 'current' | 'stale' | 'mixed' | 'unknown';
        /** Norwegian; null for the states that need no action. */
        message_no: string | null;
    };
}

/** B2 (ruling 2026-08-25 §C) — one question a SAK10 § 5-4 trigger turns on.
 *
 *  No drawing carries these and no extraction produces them, so they are ASKED —
 *  his §D principle («Det skal ikke gjette») generalised from søknadstrinn to
 *  «faktiske forhold ved saken». Only questions that gate a document for THIS
 *  project are returned, so an Oslo-only rule's question reaches Oslo and nobody
 *  else.
 *
 *  ⚠ `value` and `answered` are two fields because `null` alone is ambiguous:
 *  `answered: false` means nobody was asked, `answered: true` with `value: null`
 *  means the user answered «vet ikke». Both resolve UNDECIDED — but only one of
 *  them is still worth putting in front of someone. */
export interface UdeCaseFact {
    key: string;
    question_no: string;
    help_no?: string | null;
    /** `boolean` (Ja / Nei / Vet ikke) or `choice` (one of `options`, or Vet ikke). */
    answer_type: string;
    section_ref?: string | null;
    /** A bool for a boolean fact, an option's `value` for a choice fact. */
    value?: boolean | string | null;
    answered: boolean;
    /** The required_docs this answer decides. Shown with the question — a
     *  question is far easier to answer when you can see what it settles. */
    gates: string[];
    /** SB-282 — a choice fact's answers, served with the question so nothing
     *  here mirrors the tokens. Null for a boolean fact. */
    options?: UdeCaseFactOption[] | null;
}

export interface UdeCaseFactOption {
    value: string;
    label_no: string;
}

/** SB-71 — what discharges a vilkår (`bundle_models.VilkarKind`). Pinned by
 *  `scripts/vocabulary_parity.py`, because `VILKAR_KIND_NO` renders it. */
export type UdeVilkarKind = 'dokument' | 'annen_myndighet' | 'fysisk_arbeid' | 'henvisning' | 'anbefaling';

/** How a quote stands against the letter's own text layer (the SB-107 gate):
 *  `strict` = word for word, `words` = every word is there but not in one run,
 *  `none` = not found. `null` = the letter has no text layer to check against. */
export type UdeQuoteVerification = 'strict' | 'words' | 'none' | null;

/** SB-71 — one vilkår, verbatim, as the latest reading of its tillatelse carries it. */
export interface UdeVilkarItem {
    vilkar_hash: string;
    /** The heading it sits under, verbatim. */
    stage_heading: string | null;
    /** igangsetting | brukstillatelse | ferdigattest, read off the heading in
     *  Python; null when the heading names no stage or is not in the letter. */
    stage: string | null;
    text: string;
    kind: UdeVilkarKind;
    page: number | null;
    text_verification: UdeQuoteVerification;
    heading_verification: UdeQuoteVerification;
    picked: boolean;
}

/** SB-71 — a dispensasjon the tillatelse decides. Samsvar reads the granted
 *  ones; Kompletthet never counts them as a dispensasjon document. */
export interface UdeVilkarDispensasjon {
    parameter: string;
    status: string;
    permitted_value: number | null;
    unit: string | null;
    building: string | null;
    evidence_quote: string | null;
    source_page: number | null;
    quote_verification: UdeQuoteVerification;
}

export interface UdeVilkarDocument {
    document_id: number;
    original_filename: string | null;
    document_sha256: string;
    /** False: the row holds no vilkår claim yet (the document was re-typed to
     *  `tillatelse` by hand) — «Les vilkår» reads it, for about a cent. */
    read: boolean;
    letter_date: string | null;
    vilkar: UdeVilkarItem[];
    dispensasjoner: UdeVilkarDispensasjon[];
}

/** SB-71 — a vilkår the user follows up. It feeds this list and nothing else. */
export interface UdeVilkarPick {
    document_sha256: string;
    /** null when the letter is no longer in the project. */
    document_id: number | null;
    original_filename: string | null;
    vilkar_hash: string;
    stage_heading: string | null;
    stage: string | null;
    /** The words it was picked with, shown even when the latest reading no longer carries it. */
    text: string;
    kind: string | null;
    picked_at: string | null;
    picked_by: string | null;
    /** False: «ikke funnet i siste lesing». The pick stays until it is unpicked. */
    found: boolean;
}

export interface UdeVilkarOverview {
    documents: UdeVilkarDocument[];
    picks: UdeVilkarPick[];
}

/** SB-108 — one content point as it stands for the present document(s). */
export interface UdeSufficiencyPoint {
    item_id: string;
    description: string;
    tier: UdeChecklistTier;
    /** pass | fail | cant_determine — null when not checked (never run, or run
     *  against a bundle this document was not part of). */
    status: ComplianceVerdict | null;
    checked: boolean;
    /** `fail` only: a verified «empty» reading is cited. Without it no tier
     *  can make the failure a mangel. */
    evidence: boolean;
    reason: string | null;
    where_to_check: string | null;
    checked_at: string | null;
}

/** SB-108 — presence × sufficiency of one RequiredDoc. */
export interface UdeDocumentSufficiency {
    required_doc: string;
    state: UdeDocSufficiencyState;
    /** The rule over `state`, serialised by the backend: only `mangler` and
     *  `foreligger_mangler_opplysninger`, and only for a required doc. */
    is_mangel: boolean;
    required: boolean;
    /** The present bundle documents that satisfy the requirement. */
    documents: Array<{
        doc_id: number | string | null;
        bundle_type: string;
        original_filename: string | null;
    }>;
    points: UdeSufficiencyPoint[];
    /** Categorical Norwegian, plain text — it must survive a copy-paste. */
    note_no: string;
    /** SB-114 — set when the state is `mangler` because of an answered case
     *  fact (DiBK 2.10: the kvittering is on file and does not count), never
     *  for an absent file. The panel then spells the reason out beside the
     *  row instead of asking for an upload. */
    case_fact_key?: string | null;
}

/** SB-154 — bokstav q trinngradert. What one ansvarskategori (SØK · PRO · UTF ·
 *  KPRO · KUT, kontroll split in two since SB-161) requires at the stated
 *  søknadstrinn. A pinned mirror of
 *  `successbuilder_shared.vocabulary.ANSVAR_REQUIREMENT_STATES`
 *  (`scripts/vocabulary_parity.py`): the panel picks an icon BY THIS TOKEN, so a
 *  state present in Python and absent here would render as nothing. */
export type UdeAnsvarRequirement =
    | 'paakrevd'
    | 'kanskje'
    | 'gjelder_ikke'
    | 'ikke_paa_dette_trinnet';

export interface UdeAnsvarskategori {
    /** The canonical funksjon family — never matched on here; use `label_no`. */
    kategori: string;
    label_no: string;
    requirement: UdeAnsvarRequirement;
    /** Three-state: `null` is «ikke kontrollert» (the 10.5 readings have not
     *  been taken), never «absent». `false` needs positive evidence of absence
     *  and is never asserted for ansvarlig søker. */
    present: boolean | null;
    /** `paakrevd` AND `present === false` — serialised, never re-derived here. */
    is_mangel: boolean;
    section_ref: string;
    /** 'lawyer' = his 09.09.2026 table, or his 08.10.2026 ruling for KPRO/KUT;
     *  'ours' = a stage he did not address. */
    provenance: 'lawyer' | 'ours' | (string & {});
    note_no: string;
    /** The ramme-PRO case fact when the row is gated on one (asked on Dokumenter). */
    case_fact_key: string | null;
    evidence: {doc_id: string | null; filename: string | null; via: string}[];
}

/** Phase 15.5 + 23.1 + 23.3 — GET /completeness response. Required vs present
 *  vs missing application-bundle documents (RequiredDoc enum values). The
 *  required-set is a BASE — the SAK10 § 5-4 `matrix` set for the confirmed
 *  `tiltakstype`, else the minimal bolig `baseline` — which a kommune
 *  mangelbrev UNIONS onto (23.3: letter items lead the list; never a
 *  replacement). `required_source` names the most specific authority in play.
 *  `mangelbrev_docs` is the letter's own subset (for badging). `conditional`
 *  holds the type's trigger-gated docs («kun ved fareområde» …) — *maybe*
 *  required, absent from `required`/`missing`; a letter-listed doc is promoted
 *  out of it. `bundle_pending` > 0 ⇒ unknown uploads await the bundle pass. */
export interface UdeCompleteness {
    project_id: string;
    required: string[];
    present: string[];
    missing: string[];
    required_source: 'mangelbrev' | 'matrix' | 'baseline';
    saksnummer: string | null;
    frist: string | null;
    has_bundle: boolean;
    bundle_pending: number;
    /** The tiltakstype whose matrix set participated (alone or under a letter).
     *  Since SB-101 the GOVERNING member of the confirmed set. */
    tiltakstype: string | null;
    /** SB-101 — the whole set whose rows were unioned. Empty on the baseline. */
    tiltakstyper?: string[];
    conditional: string[];
    /** B2 (ruling 2026-08-25 §C) — docs whose § 5-4 trigger resolved FALSE
     *  against an answered case fact: «gjelder ikke». Named rather than dropped,
     *  so the subtraction stays challengeable — a reader who disagrees changes
     *  the answer that produced it. Optional: empty (and absent from older
     *  responses) until a matrix row carries a `requires_facts`. */
    not_applicable?: string[];
    mangelbrev_docs?: string[];
    /** §B (lawyer's 2026-08-19 ruling) — SAK10 § 5-4 fjerde ledd. Which stage
     *  this verdict was computed against, and the sentence that says so. Both
     *  null when the project has not stated a trinn: a completeness claim that
     *  names no scope is honest, one that implies «hele byggesøknaden» is not. */
    // OPEN vocabulary (RULING §G: «Do not infer one»), so this is a widened
    // string, not a closed union — a kommune may state `IG2`, and the backend
    // names an unrecognised trinn back to the reader rather than swallowing it.
    // `brukstillatelse` was MISSING from the closed union while the Python enum
    // carried it — the same value whose omission broke §G once already
    // ([[feedback_operator_vocabulary_silent_fail]]).
    soknadstrinn?:
        | 'ramme' | 'igangsetting' | 'ett_trinn' | 'brukstillatelse' | 'ferdigattest'
        // `endring_tillatelse` added 2026-08-28 with the ruling — he names it,
        // and it is a `ProcessCategory` in DiBK's national register. `ta`
        // (tiltak uten ansvarsrett) is in that register too and is deliberately
        // NOT here: it names an ansvarsregime, not a stage. See the Python enum.
        | 'endring_tillatelse'
        | (string & {})
        | null;
    scope_note_no?: string | null;
    /** SB-108 — one entry per doc in `required` (same order), then per
     *  present-but-not-required doc. Optional so an older backend degrades to
     *  presence alone — and then a present document is «ikke kontrollert»,
     *  never ✓. */
    sufficiency?: UdeDocumentSufficiency[];
    /** The subset of `required` in `foreligger_mangler_opplysninger` — a
     *  mangel like `missing`, kept apart because «not here» and «here, but
     *  empty» are different claims. */
    insufficient?: string[];
    /** SB-154 — bokstav q graded by trinn, one entry per ansvarskategori.
     *  `null`/absent whenever no trinn is stated or `ansvarsrett` is not
     *  required: an absent block claims nothing, an empty one would read as
     *  «nothing required». Rendered under the ansvarsrett row. */
    ansvarsrett_kategorier?: UdeAnsvarskategori[] | null;
    /** SB-161 — the subset of `required` that only SuccessBuilder's own
     *  documentation standard asks for, by no provision (the støttemur oppriss,
     *  lawyer round 5, D2). Badged on its row and named in the source line: the
     *  rule was allowed only if it is never presented as a SAK10 requirement.
     *  Absent on an older backend, which then has no such rows. */
    standard_docs?: string[];
    /** SB-216 — the § 29-4 tredje ledd b begrunnelse as a row of its own: the
     *  lawyer's «dokumentasjonsmangel» when a passage addresses the placement
     *  and gives no reason. Absent whenever the samsvarssjekk routed no building
     *  to bokstav b. Never part of `required`: «not found» is never «missing». */
    plassering_begrunnelse?: UdePlasseringBegrunnelse | null;
}

/** SB-216 — one Kompletthet row for the § 29-4 b begrunnelse. `state` is a
 *  sufficiency token (`mangler`, `foreligger_tilstrekkelig` or
 *  `foreligger_maa_kontrolleres`); only `is_mangel`, serialised by the backend,
 *  decides the warning palette. */
export interface UdePlasseringBegrunnelse {
    label_no: string;
    state: UdeDocSufficiencyState;
    is_mangel: boolean;
    note_no: string;
    section_ref: string;
    /** The buildings the run routed to bokstav b. */
    bygninger: string[];
    relieves_nabosamtykke: boolean;
    /** Set when the relief moved the nabosamtykke to «kan være påkrevd». */
    nabosamtykke_note_no?: string | null;
    documents: {doc_id: string | number | null; bundle_type: string | null;
        original_filename: string | null; status: string | null; quote: string | null;
        page: number | null}[];
}

// Phase 12.2 — per-plan grounding readiness (GET /plan-readiness).
// `review` = bestemmelser ingested but bindings held by tripped quality
// gates (12.3) — an admin approves them in questions /parameters.
export type UdePlanIngestState =
    | 'not_ingested'
    | 'pending'
    | 'ingested'
    | 'review'
    | 'failed';

export interface UdePlanReadinessEntry {
    plannavn: string;
    planid: number | null;
    state: UdePlanIngestState;
    error: string | null;
    gates_tripped: string[] | null;
    /** 12.5 — 'no_pdf' = the bestemmelser PDF couldn't be fetched (no document
     *  to open → the panel hides the «Åpne planen (PDF)» link). Only set on
     *  state==='failed'; null = a read error (the PDF exists, keep the link). */
    failure_kind: string | null;
}

export interface UdePlanReadiness {
    has_matrikkel: boolean;
    plans: UdePlanReadinessEntry[];
}

// Phase 26.1 — «Regelverk»: which documents are judging this project.
//
// NB these are NOT UdePlanIngestState. Those describe a pipeline («has the
// fetch finished?»); these describe consequence («does this document affect my
// verdicts?»), which is what the reader is asking. `ingested_unused` is a plan
// that IS in the corpus but whose bindings are held by tripped quality gates
// (12.3) — present, and not in use. Rendering that as a checkmark would be the
// overstated confidence PRODUCT.md forbids.
export type UdeRegelverkState =
    | 'grounding'          // «Grunnlag for vurderinger»
    | 'ingested_unused'    // «Lest inn, ikke i bruk»
    | 'pending'            // «Hentes inn»
    // Extraction finished but ingest never ran — the run died in between and
    // nothing re-drove it. Distinct from `missing` because the action differs:
    // finish work already done and paid for, vs obtain a document we do not
    // have. Three prod plans sat here reading «Mangler», which was advice
    // nobody could act on. NB the state name is historical: corrected at
    // questions Stage 7.5 (2026-07-30), this needs a RESUME, not an approval —
    // no person has to judge anything. Resume via questions
    // `POST /api/property-analysis/resume-stranded-stream`.
    | 'awaiting_approval'  // «Stoppet før innlesing»
    // The plan governs the property but its provisions document cannot be
    // obtained at all (LCF reports `not_covered`). Separate from `missing`
    // because «Mangler» invites action and there is none to take.
    | 'no_pdf'             // «Ingen bestemmelser-PDF»
    // The plan's own document states it has no written provisions — Oslo PBE
    // issues a one-page cover sheet saying «<plannavn> har ikke egne skrevne
    // reguleringsbestemmelser» for old plans. Not a gap and not a failure: the
    // question «which documents judge this project?» is fully answered by «this
    // one does not, and here is where it says so». Until 2026-07-31 these were
    // ingested as an empty «Cover» chapter and read «Lest inn, ikke i bruk».
    | 'no_provisions'      // «Ingen bestemmelser»
    // Wholly repealed and replaced. Its own state because the reader's next
    // question is «by what?» — which `superseded_by` answers and «Mangler» does not.
    | 'repealed'           // «Opphevet»
    | 'missing';           // «Mangler»

export interface UdeRegelverkDocument {
    doc_id: string;
    title: string;
    state: UdeRegelverkState;
    /** national tier: TEK17/SAK10/PBL always apply, so an absent one is a
     *  corpus gap rather than «not relevant here». */
    cornerstone?: boolean;
    /** kommunal tier only. */
    plannavn?: string;
    planid?: number | null;
    error?: string | null;
    failure_kind?: string | null;
    gates_tripped?: string[] | null;
    /** `awaiting_approval` only — where the reader goes to act. */
    pending_doc_id?: string | null;
    /** Live parameter_section rows / suggestions still in review, per document.
     *  **null means «not asked / could not tell», never zero.** Partial approval
     *  is the norm, not an edge case: measured across the prod corpus
     *  2026-07-30, only 2 of 7 plan documents had every binding through review,
     *  so a bare «grounding» hides the work left. */
    bindings_confirmed?: number | null;
    bindings_pending?: number | null;
}

export interface UdeRegelverkTier {
    level: 'national' | 'kommune';
    label: string;
    documents: UdeRegelverkDocument[];
}

// Phase 26.2 — the coverage register. NB `gaps: []` means «nothing was counted
// in the window», NOT «coverage is complete» — hence `counted`. Absent ≠ healthy.
export type UdeGapKind =
    | 'plan_not_ingested'   // LCF named the plan, the corpus lacks it
    | 'plan_ingest_failed'  // attempted and failed — see `detail`
    | 'plan_gate_held'      // present but grounding NOTHING (bindings gate-held)
    | 'no_plan_resolved';   // LCF resolved no plan at all — LCF coverage, not ours

export interface UdeRegelverkGap {
    kommune_code: string;
    plannavn: string;
    planid: string | null;
    gap_kind: UdeGapKind;
    detail: string | null;
    last_seen: string | null;
    /** Distinct projects that hit this gap. */
    projects: number;
    /** Distinct properties — two projects on ONE address is weaker evidence
     *  than two addresses, so both are reported rather than one blended number. */
    properties: number;
}

export interface UdeRegelverkGaps {
    window_days: number;
    gaps: UdeRegelverkGap[];
    total_projects_affected: number;
    /** Rows counted in the window. Distinguishes «no gaps» from «no traffic». */
    counted: number;
}

// Phase 26.6 — regulation a user supplied for their own property.
// `grounds_verdicts` is stated on EVERY state, never inferred from `status`:
// «klar_for_innlesing» means «this looks like the right document», NOT «and it
// is therefore now deciding your verdicts». Ingestion is a separate, gated step.
export type UdeUserRegulationStatus =
    | 'venter_pa_kontroll'      // comparison not run yet (RDE unreachable, say)
    | 'til_manuell_vurdering'   // ran, couldn't tell — the COMMON real-world case
    | 'klar_for_innlesing'      // matches the expected plan
    | 'avvist'                  // wrong / outdated / draft
    | 'feil_sted'               // a CASE document — belongs under Søknadsdokumenter
    | 'lest_inn';               // through the ingestion gate — grounds verdicts

export interface UdeUserRegulation {
    id: string;
    original_filename: string;
    size_bytes?: number;
    uploaded_at?: string | null;
    comparison_verdict: 'same' | 'different' | 'cannot_determine' | 'case_document' | null;
    comparison_reasons: string[];
    comparison_message: string | null;
    status: UdeUserRegulationStatus;
    blocking: boolean;
    message_no: string;
    grounds_verdicts: boolean;
}

export interface UdeRegelverk {
    /** false → the kommunal tier is empty pending an address; the national
     *  tier still renders, because those rules already apply. */
    has_matrikkel: boolean;
    tiers: UdeRegelverkTier[];
    summary: Record<UdeRegelverkState, number>;
}

/** Payload for PATCH /checklist/{item_id} — manual override on a single row. */
export interface UdeChecklistOverride {
    status: ComplianceVerdict;
    /** ``"_site"`` or a building_id. Defaults to ``"_site"`` server-side. */
    scope?: string;
    reason?: string;
}

// ------------------------------------------------------------------
// Phase 6 — warning resolution + locator
// ------------------------------------------------------------------

export type UdeWarningResolutionStatus = 'accepted' | 'dismissed' | 'resolved';

export interface UdeWarningResolution {
    warning_hash: string;
    status: UdeWarningResolutionStatus;
    reviewed_at: string;
    reviewed_by: string | null;
    note: string | null;
}

export interface UdeWarningStatusPatch {
    status: UdeWarningResolutionStatus;
    reviewed_by?: string | null;
    note?: string | null;
}

/** Result of POST /warnings/{hash}/locate — Tier 3 on-demand bbox. */
export interface UdeWarningLocator {
    warning_hash: string;
    bbox_xywh_normalized: number[];
    confidence: 'low' | 'medium' | 'high' | null;
    source: 'gemini_extraction' | 'tier3_localizer';
    cached: boolean;
}

/** Provenance dashboard aggregate (admin view). */
export interface UdeWarningProvenance {
    total_projects_scanned: number;
    projects_with_warnings: number;
    total_warnings: number;
    by_kind_severity: Record<string, Record<string, number>>;
    by_kind_total: Record<string, number>;
    resolved_by_kind: Record<string, Record<string, number>>;
    top_dismissed: Array<{message_no: string; count: number}>;
    locator_stats: Record<string, number>;
}

// ------------------------------------------------------------------
// Derived project status
//
// The backend has no project-level status field (decision: status is
// derived from related entities). The mapping below is the canonical
// rule used in the UI — change it here, in one place, only.
// ------------------------------------------------------------------

export type ProjectStatus = 'empty' | 'uploaded' | 'extracted' | 'checked';

export interface ProjectWithStatus extends UdeProject {
    status: ProjectStatus;
    document_count: number;
}

// ------------------------------------------------------------------
// Phase 8 Ticket 1b — per-phase status summary for nav stepper
//
// One round-trip aggregating drawings/facts/compliance status counters
// from the backend. Drives the per-phase chip text in <UdePhaseNav>.
// ------------------------------------------------------------------

export interface UdeProjectSummaryDrawings {
    total: number;
    high_confidence: number;
    needs_review: number;
    extracted: number;
    failed: number;
    pending: number;
}

export interface UdeProjectSummaryFacts {
    available: boolean;
    current_run_id: string | null;
    warnings_open: number;
    blockers_open: number;
}

export interface UdeProjectSummaryCompliance {
    validated: boolean;
    last_run_at: string | null;
    /**
     * Counts use `passed`/`failed` (not `pass`/`fail`) because `pass` is
     * a Python reserved word and the backend schema deliberately avoids
     * it. SQL status enum is still `pass`/`fail` — repository translates
     * before returning. Locked by an integration test in test_api_smoke.py.
     */
    passed: number;
    failed: number;
    cant_determine: number;
    not_applicable: number;
}

export interface UdeProjectSummary {
    drawings: UdeProjectSummaryDrawings;
    facts: UdeProjectSummaryFacts;
    compliance: UdeProjectSummaryCompliance;
}

// ------------------------------------------------------------------
// API
// ------------------------------------------------------------------

export function createUdeApi(client: AxiosInstance) {
    const base = '/ude-api';

    return {
        // Projects
        async listProjects(): Promise<UdeProject[]> {
            const response = await client.get(`${base}/projects`);
            return response.data;
        },
        async getProject(projectId: string): Promise<UdeProject> {
            const response = await client.get(`${base}/projects/${projectId}`);
            return response.data;
        },
        async createProject(payload: UdeProjectCreate): Promise<UdeProject> {
            const response = await client.post(`${base}/projects`, payload);
            return response.data;
        },
        async updateProject(projectId: string, patch: UdeProjectPatch): Promise<UdeProject> {
            const response = await client.patch(`${base}/projects/${projectId}`, patch);
            return response.data;
        },
        async deleteProject(projectId: string): Promise<void> {
            await client.delete(`${base}/projects/${projectId}`);
        },

        /**
         * Phase 8 Ticket 1b — per-phase status counters for the nav
         * stepper. Single round-trip instead of three (documents + facts +
         * checklist). Mounted on every project page so the nav can render
         * progress chips without each page caring about cross-phase state.
         */
        async getProjectSummary(projectId: string): Promise<UdeProjectSummary> {
            const response = await client.get(`${base}/projects/${projectId}/summary`);
            return response.data;
        },

        async getActiveRuns(projectId: string): Promise<UdeActiveRuns> {
            const response = await client.get(`${base}/projects/${projectId}/runs/active`);
            return response.data;
        },

        /** App-level runtime flags. ``dev_tools`` gates the /review re-merge
         *  button (and the POST /extract?source=stored path it calls). */
        async getConfig(): Promise<UdeConfig> {
            const response = await client.get(`${base}/config`);
            return response.data;
        },

        // Documents
        async listDocuments(projectId: string): Promise<UdeDocument[]> {
            const response = await client.get(`${base}/projects/${projectId}/documents`);
            return response.data;
        },
        async uploadDocuments(
            projectId: string,
            files: File[],
            onProgress?: (loaded: number, total: number) => void,
        ): Promise<UdeUploadResponse> {
            const formData = new FormData();
            for (const file of files) formData.append('files', file);
            const response = await client.post(
                `${base}/projects/${projectId}/documents`,
                formData,
                {
                    headers: {'Content-Type': 'multipart/form-data'},
                    onUploadProgress: (event) => {
                        if (onProgress && event.total) onProgress(event.loaded, event.total);
                    },
                },
            );
            return response.data;
        },
        async patchClassification(
            projectId: string,
            documentId: number,
            patch: UdeClassificationPatch,
        ): Promise<UdeDocument> {
            const response = await client.patch(
                `${base}/projects/${projectId}/documents/${documentId}`,
                patch,
            );
            return response.data;
        },
        async deleteDocument(projectId: string, documentId: number): Promise<void> {
            await client.delete(`${base}/projects/${projectId}/documents/${documentId}`);
        },

        async retryExtraction(
            projectId: string,
            documentId: number,
        ): Promise<UdeRetryExtractionResponse> {
            // Phase 7 Ticket 3c — surgical per-doc re-extract on the
            // project's most recent run. Cheap (one LLM call) compared to
            // ``startExtraction({force: true})`` (N LLM calls). Used by
            // the Drawings page's per-row "Prøv igjen" affordance.
            const response = await client.post(
                `${base}/projects/${projectId}/documents/${documentId}/retry-extraction`,
            );
            return response.data;
        },

        async retryFailedExtraction(
            projectId: string,
        ): Promise<UdeBatchRetryResponse> {
            // Phase 8 Ticket 11 — re-extract every failed drawing on the
            // most recent run in ONE pass + a single re-merge (vs. the old
            // per-row loop that re-merged N times). Backs the Drawings
            // page's "Prøv feilede på nytt" batch CTA.
            const response = await client.post(
                `${base}/projects/${projectId}/documents/retry-failed-extraction`,
            );
            return response.data;
        },

        async reclassify(
            projectId: string,
            documentId: number,
            options: {force?: boolean} = {},
        ): Promise<UdeDocument> {
            // Re-run the *classifier* on an already-uploaded PDF (no
            // re-upload), reusing the stored bytes. Recovers docs stranded
            // as unclassified by a transient classification failure, or a
            // genuine "unknown". ``force=true`` overrides a manual
            // correction (the endpoint returns 409 otherwise). Used by the
            // Drawings page's per-row "Klassifiser" affordance + the bulk
            // "Klassifiser uklassifiserte" action.
            const params = options.force ? {force: true} : undefined;
            const response = await client.post(
                `${base}/projects/${projectId}/documents/${documentId}/reclassify`,
                undefined,
                {params},
            );
            return response.data;
        },

        // Extraction
        async startExtraction(
            projectId: string,
            options: {
                force?: boolean;
                source?: 'live' | 'stored';
                regrade?: boolean;
                runId?: string;
            } = {},
        ): Promise<UdeExtractionRunStart> {
            // ``force=true`` bypasses the input-fingerprint cache and always
            // starts a new run; default behavior reuses the latest completed
            // run when documents + classifications haven't changed.
            // ``source: 'stored'`` is a dev tool (UDE_DEV_TOOLS): recompute
            // facts from the latest run's stored extractions — merge/qa/regrade
            // only, no LLM. ``regrade``/``runId`` are honored only in that mode.
            const params: Record<string, unknown> = {};
            if (options.force) params.force = true;
            if (options.source) params.source = options.source;
            if (options.regrade !== undefined) params.regrade = options.regrade;
            if (options.runId) params.run_id = options.runId;
            const response = await client.post(
                `${base}/projects/${projectId}/extract`,
                undefined,
                {params: Object.keys(params).length ? params : undefined},
            );
            return response.data;
        },
        async getExtractionRun(projectId: string, runId: string): Promise<UdeExtractionRun> {
            const response = await client.get(`${base}/projects/${projectId}/extract/${runId}`);
            return response.data;
        },

        // Facts + QA + Validate
        async getFacts(projectId: string, runId?: string): Promise<UdeProjectFacts> {
            const params = runId ? {run_id: runId} : undefined;
            const response = await client.get(`${base}/projects/${projectId}/facts`, {params});
            return response.data;
        },
        async patchFacts(projectId: string, correction: UdeFactCorrection): Promise<UdeProjectFacts> {
            const response = await client.patch(`${base}/projects/${projectId}/facts`, correction);
            return response.data;
        },
        async getDrawingQa(projectId: string): Promise<UdeDrawingQa> {
            const response = await client.get(`${base}/projects/${projectId}/drawing-qa`);
            return response.data;
        },
        async validate(projectId: string, request: UdeValidateRequest = {}): Promise<UdeValidateResponse> {
            const response = await client.post(`${base}/projects/${projectId}/validate`, request);
            return response.data;
        },
        async getValidateStatus(projectId: string): Promise<UdeValidateStatus> {
            const response = await client.get(`${base}/projects/${projectId}/validate/status`);
            return response.data;
        },
        async getChecklist(projectId: string): Promise<UdeChecklistResponse> {
            const response = await client.get(`${base}/projects/${projectId}/checklist`);
            return response.data;
        },
        /** Phase 15.5 — bundle completeness (required vs present vs missing). */
        async getCompleteness(projectId: string): Promise<UdeCompleteness> {
            const response = await client.get(`${base}/projects/${projectId}/completeness`);
            return response.data;
        },
        /** B2 — the case questions that gate a document for this project. `[]`
         *  when the tiltakstype is unconfirmed (no matrix ⇒ no triggers). */
        async getCaseFacts(projectId: string): Promise<UdeCaseFact[]> {
            const response = await client.get(`${base}/projects/${projectId}/case-facts`);
            return response.data;
        },
        /** Answer one. `null` stores «vet ikke» — a real answer, which is why it
         *  is sent rather than treated as «clear the field». A choice fact takes
         *  one of its options' `value`; anything else is a 422. */
        async answerCaseFact(
            projectId: string, key: string, value: boolean | string | null,
        ): Promise<UdeCaseFact> {
            const response = await client.put(
                `${base}/projects/${projectId}/case-facts/${encodeURIComponent(key)}`,
                {value},
            );
            return response.data;
        },
        /** SB-71 — the vilkår of the project's tillatelser, and the picks. */
        async getVilkar(projectId: string): Promise<UdeVilkarOverview> {
            const response = await client.get(`${base}/projects/${projectId}/vilkar`);
            return response.data;
        },
        /** SB-71 — pick or unpick one vilkår. Returns the overview as it now
         *  stands. A pick can only be made on a vilkår the letter carries. */
        async putVilkarPick(
            projectId: string, documentSha256: string, vilkarHash: string, picked: boolean,
        ): Promise<UdeVilkarOverview> {
            const response = await client.put(`${base}/projects/${projectId}/vilkar/picks`, {
                document_sha256: documentSha256,
                vilkar_hash: vilkarHash,
                picked,
                picked_by: 'frontend',
            });
            return response.data;
        },
        /** SB-71 — «Les vilkår»: one paid model call (about a cent) for a
         *  tillatelse whose row holds no vilkår claim yet. One call, seconds,
         *  so it is awaited; the server writes the claim before it answers. */
        async readVilkar(projectId: string, documentId: number): Promise<UdeVilkarOverview> {
            const response = await client.post(
                `${base}/projects/${projectId}/documents/${documentId}/read-vilkar`,
            );
            return response.data;
        },
        /** Phase 12.2 — per-plan grounding readiness. Poll while any plan is
         *  `pending` (eager ingestion is multi-minute). */
        async getPlanReadiness(projectId: string): Promise<UdePlanReadiness> {
            const response = await client.get(`${base}/projects/${projectId}/plan-readiness`);
            return response.data;
        },
        /** Phase 26.6 — supply regulation for this property. Stored and
         *  compared; grounds NOTHING until it passes the ingestion gate. */
        async uploadRegulation(projectId: string, file: File): Promise<UdeUserRegulation> {
            const form = new FormData();
            form.append('file', file);
            const response = await client.post(
                `${base}/projects/${projectId}/regulations`, form,
            );
            return response.data;
        },
        async listRegulations(projectId: string): Promise<{documents: UdeUserRegulation[]}> {
            const response = await client.get(`${base}/projects/${projectId}/regulations`);
            return response.data;
        },
        /** Phase 26.8 — hand a user-supplied document to the ingestion queue,
         *  scoped to this property alone. Does NOT approve: grounding begins
         *  when a human approves it in Doc Processing. */
        async submitRegulation(projectId: string, regulationId: string): Promise<{
            pending_doc_id: string; doc_id: string;
            grounds_verdicts: boolean; message_no: string;
        }> {
            const response = await client.post(
                `${base}/projects/${projectId}/regulations/${regulationId}/submit`,
            );
            return response.data;
        },
        async deleteRegulation(projectId: string, regulationId: string): Promise<void> {
            await client.delete(`${base}/projects/${projectId}/regulations/${regulationId}`);
        },
        /** Phase 26.2 — «N projects wanted regulation we don't hold», worst
         *  first. Admin-only; window-scoped. */
        async getRegelverkGaps(days = 90): Promise<UdeRegelverkGaps> {
            const response = await client.get(`${base}/admin/regelverk-gaps`, {params: {days}});
            return response.data;
        },
        /** Phase 26.1 — the project's regulatory basis, national → kommunal.
         *  Poll while anything is `pending` (plan ingestion is multi-minute). */
        async getRegelverk(projectId: string): Promise<UdeRegelverk> {
            const response = await client.get(`${base}/projects/${projectId}/regelverk`);
            return response.data;
        },
        /** Phase 12.2 «Les inn på nytt» — re-fire eager plan ingestion for the
         *  project's address (bypasses the failure backoff; deduped server-side). */
        async retryPlanIngestion(projectId: string): Promise<{scheduled: boolean}> {
            const response = await client.post(`${base}/projects/${projectId}/plan-ingestion/retry`);
            return response.data;
        },
        async patchChecklistItem(
            projectId: string,
            itemId: string,
            override: UdeChecklistOverride,
        ): Promise<{ok: boolean; project_id: string; item_id: string; scope: string}> {
            const response = await client.patch(
                `${base}/projects/${projectId}/checklist/${itemId}`,
                override,
            );
            return response.data;
        },

        // ----------------------------------------------------------------
        // Phase 6 — warning resolutions + locator + audit + PDF stream
        // ----------------------------------------------------------------

        /** Map of warning_hash → resolution row for the project. */
        async getWarningResolutions(
            projectId: string,
        ): Promise<Record<string, UdeWarningResolution>> {
            const response = await client.get(
                `${base}/projects/${projectId}/warning-resolutions`,
            );
            return response.data;
        },

        /** Mark a warning as accepted/dismissed/resolved (or remove resolution). */
        async patchWarningStatus(
            projectId: string,
            warningHash: string,
            patch: UdeWarningStatusPatch,
        ): Promise<UdeWarningResolution> {
            const response = await client.patch(
                `${base}/projects/${projectId}/warnings/${warningHash}/status`,
                patch,
            );
            return response.data;
        },

        /** Remove an existing resolution (un-dismiss). */
        async deleteWarningResolution(
            projectId: string,
            warningHash: string,
        ): Promise<void> {
            await client.delete(
                `${base}/projects/${projectId}/warnings/${warningHash}/status`,
            );
        },

        /**
         * Tier 3 on-demand localization. Issues a Gemini Flash call asking
         * for the bbox of `affected_value_label` on `page_number` of the
         * source PDF. Cached in ude.warning_locator_cache so repeat calls
         * are free.
         */
        async locateWarning(
            projectId: string,
            warningHash: string,
        ): Promise<UdeWarningLocator> {
            const response = await client.post(
                `${base}/projects/${projectId}/warnings/${warningHash}/locate`,
            );
            return response.data;
        },

        /** Full audit list of resolutions for the project. */
        async getWarningAudit(projectId: string): Promise<UdeWarningResolution[]> {
            const response = await client.get(
                `${base}/projects/${projectId}/warnings/audit`,
            );
            return response.data;
        },

        /**
         * Stream a project's PDF as raw bytes for the peek-panel viewer.
         * Returns a Uint8Array that gets passed directly to react-pdf as
         * `<Document file={{data}}>` — avoids blob-URL hops which can fail
         * silently across pdfjs-dist worker boundaries.
         */
        async getDocumentPdfBytes(
            projectId: string,
            documentId: number,
        ): Promise<Uint8Array> {
            const response = await client.get(
                `${base}/projects/${projectId}/documents/${documentId}/pdf`,
                {responseType: 'arraybuffer'},
            );
            return new Uint8Array(response.data);
        },

        // ----------------------------------------------------------------
        // Phase 6.5 — admin provenance aggregation
        // ----------------------------------------------------------------

        async getWarningProvenance(): Promise<UdeWarningProvenance> {
            const response = await client.get(`${base}/admin/warnings/provenance`);
            return response.data;
        },

        // SSE for extraction progress (raw fetch — same pattern as analyzePropertyStream)
        streamExtractionProgress(
            projectId: string,
            runId: string,
            callbacks?: {
                onProgress?: (event: {progress_done: number; progress_total: number; status: ExtractionStatus}) => void;
                onComplete?: (run: UdeExtractionRun) => void;
                onError?: (error: string) => void;
            },
        ): {abort: () => void} {
            const abortController = new AbortController();
            authFetch(`${base}/projects/${projectId}/extract/${runId}/stream`, {
                signal: abortController.signal,
            })
                .then(async (response) => {
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    if (!response.body) throw new Error('No response body');
                    const reader = response.body.getReader();
                    const decoder = new TextDecoder();
                    let buffer = '';
                    let ended = false;
                    const processLine = (line: string) => {
                        if (!line.startsWith('data: ')) return;
                        try {
                            const data = JSON.parse(line.slice(6));
                            if (data.status === 'completed' || data.status === 'failed') {
                                ended = true;
                                callbacks?.onComplete?.(data);
                            } else {
                                callbacks?.onProgress?.(data);
                            }
                        } catch { /* ignore parse errors */ }
                    };
                    while (true) {
                        const {done, value} = await reader.read();
                        if (done) break;
                        buffer += decoder.decode(value, {stream: true});
                        const lines = buffer.split('\n');
                        buffer = lines.pop() || '';
                        for (const line of lines) if (line.trim()) processLine(line);
                    }
                    // The server closes the stream only after the terminal event.
                    // Any other close (a UDE restart, a proxy) left the caller
                    // waiting on numbers that would never move (SB-26).
                    if (!ended) throw new Error('Strømmen ble avbrutt før uthentingen var ferdig');
                })
                .catch((error) => {
                    if (error.name !== 'AbortError') callbacks?.onError?.(error.message || 'Stream failed');
                });
            return {abort: () => abortController.abort()};
        },
    };
}

// ------------------------------------------------------------------
// Derived helpers — pure functions, no side effects
// ------------------------------------------------------------------

/**
 * Derive a project-level status from its document count + extraction state.
 * Mapping:
 *   no documents      → 'empty'
 *   has documents     → 'uploaded'
 *   has facts         → 'extracted'
 *   has checklist     → 'checked'
 *
 * `extracted` and `checked` need separate API calls per project; for the
 * list view we only know `empty` vs `uploaded` from the documents count.
 * The richer states are added once a project is opened.
 */
export function deriveProjectStatus(docCount: number, hasFacts: boolean, hasChecklist: boolean): ProjectStatus {
    if (hasChecklist) return 'checked';
    if (hasFacts) return 'extracted';
    if (docCount > 0) return 'uploaded';
    return 'empty';
}
